<script setup lang="ts">
import VsText from '@/components/vision/VsText.vue'
import { computed, reactive, ref } from 'vue'
import { useActions, useGetters, useStore } from 'vuex-composition-helpers'
import Job, { ApprovalStatus, JobStatus } from '@/models/job'
import Company from '@/models/company'
import { saveFile } from '@/utils/save-file'
import {
  getJobPdf,
  useBulkArchiveMutation,
  useGetJobStopsQuery,
  useUnarchiveMutation,
} from '@/api/jobs'
import { SnackType } from '@/components/vision/SnackBarType'
import VsButton from '@/components/vision/VsButton.vue'
import DisabledActionTooltip from '@/components/common/DisabledActionTooltip.vue'
import { useRoute, useRouter } from 'vue-router/composables'
import VsMenu from '@/components/vision/VsMenu.vue'
import { useRemoveJobsFromInvoiceMutation } from '@/api/invoice'
import { useBusinessUnitPermissions } from '@/components/tickets/common/useBusinessUnitPermissions'
import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'
import { useJobSearchRouteContext } from '@/common/useJobSearchRouteContext'
import { useJobStopsVisibilityLogic } from '@/components/tickets/StopsAndInventories/useJobStopsVisibilityLogic'
import { InventoryReceiptType, JobStopType } from '@ironsight/types'

type MenuItem = {
  text: string
  icon: string
  onClick: () => void
}

const route = useRoute()
const router = useRouter()
const { batchRouteName } = useJobSearchRouteContext()

interface Props {
  jobModel: Job
  company: Company | undefined
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'refresh-job-history')
  (
    e: 'handle-snackbar',
    value: {
      type?: SnackType
      messageKey?: string | null
      customMessage?: string
    },
  )
  (e: 'open-email-dialog')
  (e: 'update-readonly-job')
}>()

const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()

const isApproving = ref(false)
const isSubmittingForApproval = ref(false)
const isPdfDownloading = ref(false)

const menuItems = computed(() => {
  const items: MenuItem[] = []
  if (jobIsApproved.value && canUnapprove.value) {
    items.push({
      text: 'Unapprove',
      icon: 'mdi-lock-open-outline',
      onClick: unapprove,
    })
  }
  if (canArchiveJob.value(job.value.hubId, job.value.accountId)) {
    items.push({
      text: archiveBtnLabel.value,
      icon: archiveBtnIcon.value,
      onClick: archiveBtnClicked,
    })
  }
  if (job.value.invoiceId && canEditBatch.value(job.value.serviceProviderBusinessUnitId)) {
    items.push({
      text: 'Remove from batch',
      icon: 'mdi-link-variant-off',
      onClick: removeFromBatch,
    })
  }
  if (job.value.invoiceId && canViewBatch.value(job.value.serviceProviderBusinessUnitId)) {
    items.push({
      text: 'View batch',
      icon: 'mdi-wallet-bifold-outline',
      onClick: viewBatch,
    })
  }
  if (showSecondaryAddToBatchAction.value) {
    items.push({
      text: 'Add to batch',
      icon: 'mdi-wallet-bifold-outline',
      onClick: openAddToBatchBottomSheet,
    })
  }
  if (showSecondaryApproveAction.value) {
    items.push({
      text: 'Approve',
      icon: 'mdi-check-all',
      onClick: approve,
    })
  }
  if (showCancelApprovalRequestAction.value) {
    items.push({
      text: 'Cancel approval request',
      icon: 'mdi-undo',
      onClick: cancelApprovalRequest,
    })
  }
  if (showUnCompleteJobRequestAction.value) {
    items.push({
      text: 'Reopen',
      icon: 'mdi-backup-restore',
      onClick: unCompleteJobRequest,
    })
  }

  return items
})

const {
  canApproveJob,
  canSubmitForApproval,
  canArchiveJob,
  canUnapproveJob,
  canViewBatch,
  canViewAddToBatchSelectionPage,
  isThirdPartyJobViewer,
} = useGetters('permissions', [
  'canApproveJob',
  'canSubmitForApproval',
  'canArchiveJob',
  'canUnapproveJob',
  'canViewBatch',
  'canViewAddToBatchSelectionPage',
  'isThirdPartyJobViewer',
])
const { approveJob, submitJobForApproval, cancelJobApprovalRequest, unCompleteJob, unapproveJob } =
  useActions('jobs', [
    'approveJob',
    'submitJobForApproval',
    'cancelJobApprovalRequest',
    'unCompleteJob',
    'unapproveJob',
  ])

const store = useStore()
const canReopenJob = computed<(hubId: string, jobAccountId: string) => boolean>(
  () => store.getters['permissions/canReopenJob'],
)
const canEditBatch = computed<(batchOwnerId: string | null) => boolean>(
  () => store.getters['permissions/canEditBatch'],
)

const ticketErrors = computed(() => store.getters['tickets/getError'])

const job = computed(() => reactive(props.jobModel))
const jobUuid = computed(() => job.value.uuid)
const { data: stopsData } = useGetJobStopsQuery(jobUuid)
const dropoffStop = computed(
  () => stopsData.value?.find((s) => s.stopType === JobStopType.DropOff) ?? null,
)

const hasPdfs = computed(() => pdfFormats.value.length > 0)
const pdfFormats = computed(() =>
  (
    props.company?.pdfFormats?.filter((p) => job.value.activity?.pdfFormatIds.includes(p.id)) ?? []
  ).sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1)),
)

const jobIsApproved = computed(() => job.value.isApproved)
const showApproveAction = computed(
  (): boolean =>
    job.value.isPendingApproval && canApproveJob.value(job.value.hubId, job.value.accountId),
)
const showSecondaryApproveAction = computed(
  () =>
    !job.value.isPendingApproval &&
    !job.value.isApproved &&
    job.value.status === JobStatus.COMPLETED &&
    canApproveJob.value(job.value.hubId, job.value.accountId),
)
const showSubmitForApproveAction = computed(
  (): boolean =>
    job.value.isCompleted &&
    !(job.value.isPendingApproval || job.value.isApproved) &&
    canSubmitForApproval.value(job.value.serviceProviderBusinessUnitId),
)
const { canSendEmailForApproval } = useBusinessUnitPermissions()
const showSendEmailForApproveAction = computed(
  () => showSubmitForApproveAction.value && canSendEmailForApproval.value,
)
const showCancelApprovalRequestAction = computed(
  (): boolean =>
    job.value.isCompleted &&
    job.value.isPendingApproval &&
    canSubmitForApproval.value(job.value.serviceProviderBusinessUnitId),
)
const showAddToBatchAction = computed(
  (): boolean => job.value.invoiceId === null && canViewAddToBatchSelectionPage.value,
)
const showSecondaryAddToPdf = computed(
  () =>
    (showSubmitForApproveAction.value ||
      showApproveAction.value ||
      showAddToBatchAction.value ||
      showDropOffReceiptAction.value) &&
    hasPdfs.value,
)
const showSecondaryAddToBatchAction = computed(
  () => (showSubmitForApproveAction.value || showApproveAction.value) && showAddToBatchAction.value,
)
const showUnCompleteJobRequestAction = computed(
  () =>
    job.value.status === JobStatus.COMPLETED &&
    job.value.approvalStatus !== ApprovalStatus.APPROVED &&
    !job.value.isArchived &&
    canReopenJob.value(job.value.hubId, job.value.accountId),
)
const canUnapprove = computed(
  () =>
    jobIsApproved.value &&
    canUnapproveJob.value(job.value.hubId, job.value.approvedBy, job.value.accountId),
)

const thirdPartyJobViewer = computed(() =>
  isThirdPartyJobViewer.value(job.value.accountId, job.value.serviceProviderBusinessUnitId),
)

const {
  shouldShowReceiverReceiptButton: showDropOffReceiptAction,
  receiverReceiptPermission,
  canConfirmReceiverReceipt,
} = useJobStopsVisibilityLogic(
  dropoffStop,
  computed(() => job.value.id),
)

const archiveBtnIcon = computed(() =>
  job.value.isArchived ? 'mdi-archive-arrow-up-outline' : 'mdi-archive-arrow-down-outline',
)
const archiveBtnLabel = computed(() => (job.value.isArchived ? 'Unarchive' : 'Archive'))

function emitActionError(messageKey: string, customMessage?: string) {
  if (customMessage) {
    emit('handle-snackbar', {
      customMessage,
      type: SnackType.ERROR,
    })
    return
  }

  emit('handle-snackbar', {
    messageKey,
    type: SnackType.ERROR,
  })
}

async function submitForApproval() {
  isSubmittingForApproval.value = true
  try {
    await submitJobForApproval({ uuid: job.value.uuid })
    emit('update-readonly-job')
    emit('refresh-job-history')
    emit('handle-snackbar', {
      type: SnackType.INFO,
      messageKey: 'submittedForApproval',
    })
  } catch (error) {
    emitActionError('submitForApprovalFailed')
  } finally {
    isSubmittingForApproval.value = false
  }
}

async function cancelApprovalRequest() {
  try {
    await cancelJobApprovalRequest({ uuid: job.value.uuid })
    emit('update-readonly-job')
    emit('refresh-job-history')
    emit('handle-snackbar', {
      type: SnackType.INFO,
      messageKey: 'cancelApprovalRequest',
    })
  } catch (error) {
    emitActionError('cancelApprovalRequestFailed')
  }
}

async function unCompleteJobRequest() {
  await unCompleteJob({ jobNumber: job.value.id })
  if (!ticketErrors.value) {
    emit('update-readonly-job')
    emit('refresh-job-history')
    emit('handle-snackbar', {
      type: SnackType.INFO,
      messageKey: 'unCompleteJobRequest',
    })
  } else {
    emitActionError('unCompleteJobRequestFailed')
  }
}

async function approve() {
  isApproving.value = true
  try {
    await approveJob({ id: job.value.id })
    emit('update-readonly-job')
    emit('refresh-job-history')
    emit('handle-snackbar', { type: SnackType.INFO, messageKey: 'approved' })
  } catch (error) {
    const errorKey = 'approvalFailed'
    const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
    if (detail) {
      emitActionError(errorKey, detail.toString())
    } else {
      emitActionError(errorKey)
    }
  } finally {
    isApproving.value = false
  }
}

async function downloadPdf(pdfFormatId: string) {
  isPdfDownloading.value = true

  try {
    saveFile(await getJobPdf(job.value.uuid, pdfFormatId))
  } catch {
    emitActionError('pdfDownloadError')
  }

  isPdfDownloading.value = false
}

function choosePDFFormat(openPdfMenu: () => void) {
  if (pdfFormats.value.length === 1) {
    downloadPdf(pdfFormats.value[0].id)
  } else {
    openPdfMenu()
  }
}

async function unapprove() {
  isApproving.value = true
  try {
    await unapproveJob({ id: job.value.id })
    emit('update-readonly-job')
    emit('refresh-job-history')
    emit('handle-snackbar', { type: SnackType.INFO, messageKey: 'unapproved' })
  } catch (error) {
    emitActionError('unapprovalFailed')
  } finally {
    isApproving.value = false
  }
}

const { mutateAsync: bulkArchiveAsync } = useBulkArchiveMutation()
const { mutateAsync: unarchiveAsync } = useUnarchiveMutation()

async function archiveBtnClicked() {
  const successKey = job.value.isArchived ? 'unarchived' : 'archived'
  const errorKey = job.value.isArchived ? 'unarchiveFailed' : 'archiveFailed'

  try {
    if (job.value.isArchived) {
      await unarchiveAsync({ jobId: job.value.uuid })
    } else {
      await bulkArchiveAsync({ jobIds: [job.value.uuid] })
    }
    emit('update-readonly-job')
    emit('refresh-job-history')
    emit('handle-snackbar', { type: SnackType.INFO, messageKey: successKey })
  } catch (error) {
    const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
    if (detail) {
      emitActionError(errorKey, detail.toString())
    } else {
      emitActionError(errorKey)
    }
  }
}

function clone() {
  router.push({
    name: (route.meta?.cloneRouteName as string | undefined) ?? route.name ?? undefined,
    params: { id: 'new', hubId: job.value.hubId },
    query: { fromJob: job.value.id.toString() },
  })
}

const { mutateAsync: removeJobsFromBatch } = useRemoveJobsFromInvoiceMutation()
const removeFromBatch = async () => {
  if (!job.value.invoiceId) return
  try {
    await removeJobsFromBatch({
      id: job.value.invoiceId ?? '',
      jobIds: [job.value.uuid],
    })
    emit('update-readonly-job')
    emit('refresh-job-history')
    emit('handle-snackbar', {
      type: SnackType.INFO,
      messageKey: 'removedJobFromBatch',
    })
  } catch {
    emitActionError('removedJobFromBatchFailure')
  }
}

const viewBatch = async () => {
  if (!job.value.invoiceId) return
  const batchId = job.value.invoiceId ?? ''
  await router.push({ name: batchRouteName.value, params: { batchId } })
}

function openDropOffReceiptBottomSheet() {
  if (!canConfirmReceiverReceipt.value) return
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    stopConfirmationBottomSheetStates: {
      isVisible: true,
      requestedStop: null,
      actualStop: dropoffStop.value,
      receiptType: InventoryReceiptType.Receiver,
    },
  })
}

function openRejectionBottomSheet() {
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    isRejectingApprovalBottomSheetVisible: true,
  })
}

function openAddToBatchBottomSheet() {
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    isBatchBottomSheetVisible: true,
  })
}

const onClickOpenSendForApprovalBottomSheet = () => {
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    isSendingEmailForApprovalBottomSheetVisible: true,
  })
}
</script>

<template>
  <vs-wrapper>
    <v-row v-if="showSendEmailForApproveAction">
      <v-col>
        <vs-button
          :loading="isSubmittingForApproval"
          full-width
          icon="mdi-checkbox-marked-outline"
          label="Mark as pending approval"
          @click="submitForApproval"
        />
      </v-col>
      <v-col>
        <vs-button
          full-width
          icon="mdi-send"
          label="Send for approval"
          type="secondary"
          @click="onClickOpenSendForApprovalBottomSheet"
        />
      </v-col>
    </v-row>
    <v-row v-else-if="showSubmitForApproveAction">
      <v-alert
        v-if="job.isApprovalRejected"
        border="bottom"
        class="ml-1 mr-1"
        colored-border
        elevation="1"
        style="width: 100%"
        type="warning"
      >
        <vs-text type="overline">Approval rejected</vs-text>
        <vs-text
          >{{
            job.approvalRejectionReason
              ? job.approvalRejectionReason
              : 'No additional information provided.'
          }}
        </vs-text>
      </v-alert>
      <vs-button
        :label="job.isApprovalRejected ? 'Resubmit for approval' : 'Submit for approval'"
        :loading="isSubmittingForApproval"
        data-test="readonly-submit-for-approval"
        full-width
        icon="mdi-checkbox-marked-outline"
        @click="submitForApproval"
      />
    </v-row>

    <v-row v-else-if="showApproveAction">
      <v-col>
        <vs-button
          data-test="readonly-reject"
          destructive
          full-width
          icon="mdi-close"
          label="Reject"
          type="secondary"
          @click="openRejectionBottomSheet"
        />
      </v-col>
      <v-col>
        <vs-button
          :loading="isApproving"
          data-test="readonly-approve"
          full-width
          icon="mdi-check-all"
          label="Approve"
          type="primary"
          @click="approve"
        />
      </v-col>
    </v-row>

    <v-row v-else-if="showAddToBatchAction">
      <vs-button
        data-test="readonly-add-to-invoice-button"
        full-width
        label="Add to batch"
        @click="openAddToBatchBottomSheet"
      />
    </v-row>
    <v-row v-else-if="showDropOffReceiptAction">
      <disabled-action-tooltip
        :permission="receiverReceiptPermission"
        label="Confirm Receipt"
        full-width
      >
        <template #default="{ disabled }">
          <vs-button
            data-test="readonly-drop-off-receipt-button"
            :disabled="disabled"
            full-width
            label="Confirm Receipt"
            @click="openDropOffReceiptBottomSheet"
          />
        </template>
      </disabled-action-tooltip>
    </v-row>

    <v-menu v-else-if="hasPdfs" offset-y>
      <template #activator="{ on }">
        <vs-button
          :loading="isPdfDownloading"
          data-test="readonly-download-pdf"
          full-width
          label="Download PDF"
          @click="(e) => choosePDFFormat(() => on.click(e))"
        />
      </template>
      <v-list>
        <v-list-item
          v-for="format in pdfFormats"
          :key="format.id"
          data-test="pdf-option"
          @click="downloadPdf(format.id)"
        >
          <v-list-item-title>{{ format.name }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <v-row ref="secondary_actions" class="my-2" justify="center">
      <v-col v-if="!thirdPartyJobViewer" class="shrink">
        <vs-button
          class="btn mx-4"
          data-test="readonly-clone-button"
          icon="content_copy"
          type="secondary"
          @click="clone"
        />
        <vs-text class="btn-text">Clone</vs-text>
      </v-col>
      <v-col v-if="showSecondaryAddToPdf" class="shrink">
        <v-menu offset-y>
          <template #activator="{ on }">
            <vs-button
              :loading="isPdfDownloading"
              class="btn mx-4"
              icon="mdi-file-pdf-box "
              type="secondary"
              @click="(e) => choosePDFFormat(() => on.click(e))"
            />
          </template>
          <v-list>
            <v-list-item
              v-for="format in pdfFormats"
              :key="format.id"
              @click="downloadPdf(format.id)"
            >
              <v-list-item-title>{{ format.name }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <vs-text class="btn-text">Download<br />PDF</vs-text>
      </v-col>
      <v-col class="shrink">
        <vs-button
          class="btn mx-4"
          icon="mdi-send"
          type="secondary"
          @click="emit('open-email-dialog')"
        />
        <vs-text class="btn-text ml-1">Notify</vs-text>
      </v-col>
      <v-col v-if="menuItems.length > 1" class="shrink">
        <vs-menu :items="menuItems" class="ml-4" data-test="readonly-more-button" />
        <vs-text class="btn-text ml-4">More</vs-text>
      </v-col>
      <v-col v-else-if="menuItems.length === 1" class="shrink">
        <vs-button
          :icon="menuItems[0].icon"
          class="btn mx-4"
          type="secondary"
          @click="menuItems[0].onClick"
        />
        <vs-text class="btn-text ml-1">{{ menuItems[0].text }}</vs-text>
      </v-col>
    </v-row>
  </vs-wrapper>
</template>

<style scoped>
.btn {
  height: 48px;
  width: 48px;
}

.btn-text {
  text-align: center;
  color: var(--color-blue);
  font-weight: bold;
}
</style>
